/** Procès-verbal : consultation, impression, export. */

import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { buildPV, pvToText } from '../../core/index.js';
import { StoreElement } from '../base.js';
import { nomFichier, telecharger } from '../format.js';
import { ouvrirImpression } from '../impression.js';

@customElement('view-pv')
export class ViewPV extends StoreElement {
  private imprimer(): void {
    const pv = buildPV(this.store.state, this.store.computation);
    ouvrirImpression(`Procès-verbal — ${pv.nom}`, `<h1>${pv.nom}</h1><pre>${pvToText(pv).replace(/[<>&]/g, '')}</pre>`);
  }

  override render() {
    const { state, computation } = this.store;
    const pv = buildPV(state, computation);

    return html`
      <div class="page page--narrow">
        <div class="row row--between no-print">
          <h1 class="t-headline">Procès-verbal</h1>
          <div class="row">
            <md-text-button @click=${() => void this.store.reload()}>Rafraîchir</md-text-button>
            <md-outlined-button @click=${() => this.imprimer()}>Imprimer</md-outlined-button>
            <md-text-button
              @click=${() => telecharger(nomFichier(`journal-${pv.nom}`, 'json'), JSON.stringify(this.store.events, null, 2))}
              >Exporter le journal</md-text-button
            >
          </div>
        </div>

        <section class="card">
          <p class="t-label">Élection</p>
          <h2 class="t-title">${pv.nom}</h2>
          <p class="t-body">
            ${pv.classe} · ${pv.etablissement}<br />
            <span class="t-muted">Date prévue : ${pv.date} · Phase : ${pv.phase}</span>
          </p>
          <p class="t-body t-muted">Référence : ${pv.reference}</p>
        </section>

        ${pv.tours.map(
          (tour) => html`
            <section class="card">
              <h2 class="t-title">${tour.titre}</h2>
              <p class="t-body t-muted">
                ${tour.voixParElecteur} voix par électeur · ${tour.seuil}
              </p>
              <div class="stats">
                <div class="stat"><div class="stat__value">${tour.chiffres.inscrits}</div><div class="stat__label">Inscrits</div></div>
                <div class="stat"><div class="stat__value">${tour.chiffres.votants}</div><div class="stat__label">Votants</div></div>
                <div class="stat"><div class="stat__value">${tour.chiffres.valides}</div><div class="stat__label">Valides</div></div>
                <div class="stat"><div class="stat__value">${tour.chiffres.blancs}</div><div class="stat__label">Blancs</div></div>
                <div class="stat"><div class="stat__value">${tour.chiffres.nuls}</div><div class="stat__label">Nuls</div></div>
                <div class="stat"><div class="stat__value">${tour.chiffres.complets}</div><div class="stat__label">Complets</div></div>
                <div class="stat"><div class="stat__value">${tour.chiffres.partiels}</div><div class="stat__label">Partiels</div></div>
                <div class="stat"><div class="stat__value">${tour.chiffres.participation}</div><div class="stat__label">Participation</div></div>
              </div>
              <ul class="list">
                ${tour.classement.map(
                  (entry) => html`
                    <li class="list__item">
                      <span class="grow">${entry.label}</span>
                      <span class="pill ${entry.majoritaire ? 'pill--ok' : ''}">${entry.voix} voix${entry.majoritaire ? ' · majorité absolue' : ''}</span>
                    </li>
                  `,
                )}
              </ul>
              ${tour.nulsParMotif.length > 0
                ? html`<p class="t-body t-muted">
                    Nuls : ${tour.nulsParMotif.map((entry) => `${entry.motif} ×${entry.nombre}`).join(', ')}
                  </p>`
                : nothing}
            </section>
          `,
        )}

        <section class="card ${pv.ecart ? 'card--warn' : 'card--ok'}">
          <h2 class="t-title">${pv.ecart ? 'Écart entre le calcul et le résultat retenu' : 'Résultat retenu'}</h2>
          ${pv.ecart
            ? html`<p class="t-body">
                Le résultat retenu diffère du dépouillement. Les deux figurent ci-dessous : rien n’est effacé.
              </p>`
            : nothing}
          <div class="grid-2">
            <div>
              <p class="t-label">Résultat calculé</p>
              <ul class="list">
                ${pv.siegeCalcules.length === 0
                  ? html`<li class="t-body t-muted">Aucun siège</li>`
                  : pv.siegeCalcules.map(
                      (seat) => html`<li class="list__item"><span class="pill">${seat.role}</span><span class="grow">${seat.personne}</span></li>`,
                    )}
              </ul>
            </div>
            <div>
              <p class="t-label">Résultat retenu</p>
              <ul class="list">
                ${pv.siegeRetenus.length === 0
                  ? html`<li class="t-body t-muted">Aucun siège</li>`
                  : pv.siegeRetenus.map(
                      (seat) => html`<li class="list__item"><span class="pill ${seat.origine === 'décision de l’enseignant' ? 'pill--warn' : 'pill--ok'}">${seat.role}</span><span class="grow">${seat.personne}</span></li>`,
                    )}
              </ul>
            </div>
          </div>
        </section>

        ${pv.decisions.length > 0
          ? html`<section class="card">
              <h2 class="t-title">Décisions souveraines de l’enseignant</h2>
              <ul class="list">
                ${pv.decisions.map((decision) => html`<li class="list__item">${decision}</li>`)}
              </ul>
            </section>`
          : nothing}
        ${pv.designations.length > 0
          ? html`<section class="card">
              <h2 class="t-title">Désignations de suppléant</h2>
              <ul class="list">${pv.designations.map((entry) => html`<li class="list__item">${entry}</li>`)}</ul>
            </section>`
          : nothing}
        ${pv.mandats.length > 0
          ? html`<section class="card">
              <h2 class="t-title">Acceptation des mandats</h2>
              <ul class="list">${pv.mandats.map((entry) => html`<li class="list__item">${entry}</li>`)}</ul>
            </section>`
          : nothing}
        ${pv.departages.length > 0
          ? html`<section class="card">
              <h2 class="t-title">Départages</h2>
              <ul class="list">${pv.departages.map((entry) => html`<li class="list__item">${entry}</li>`)}</ul>
            </section>`
          : nothing}
        ${pv.egaliteEnCours.length > 0
          ? html`<section class="card card--warn">
              <h2 class="t-title">Égalité non tranchée</h2>
              <p class="t-body">${pv.egaliteEnCours.join(', ')}</p>
            </section>`
          : nothing}

        <section class="card card--flat">
          <p class="t-body t-mono">Empreinte du résultat : ${pv.empreinte}</p>
          <p class="t-body t-mono">Empreinte du journal : ${pv.empreinteJournal}</p>
          <p class="t-body t-muted">
            Créée le ${pv.creeLe}. Données supprimées après le ${pv.purgeLe}. Aucune copie ne subsiste ailleurs :
            le scrutin ne repose sur aucun serveur.
          </p>
        </section>
      </div>
    `;
  }
}
